import { useQueryClient } from "@tanstack/react-query";
import { useAppPathname as usePathname } from "@/lib/navigation/use-app-pathname";
import { useAppSearchParams as useSearchParams } from "@/lib/navigation/use-app-search-params";
import { useEffect, useRef } from "react";

import { useAccessSession } from "@/components/providers/access-session";
import {
  clearTikTokOAuthFlow,
  readTikTokOAuthWorkspace,
} from "@/lib/tiktok/oauth-return-session";
import { toStaticExportHref } from "@/config/normalize-application-pathname";
import { refreshTikTokConnectionAfterOAuthReturn } from "@/lib/tiktok/oauth-return-sync";
import { useAppRouter } from "@/lib/navigation/use-app-router";

function buildReturnKey(searchParams: URLSearchParams | null): string | null {
  const oauthReturn = searchParams?.get("tiktok");
  if (oauthReturn !== "connected") return null;
  const token = searchParams?.get("oauth");
  return token ? `connected:${token}` : "connected";
}

/** OAuth 成功回跳时刷新连接快照并清理 URL，不再轮询后端。 */
export function TikTokOAuthReturnHandler() {
  const queryClient = useQueryClient();
  const router = useAppRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { membership } = useAccessSession();
  const defaultWorkspaceId = membership?.workspace.id;
  const returnKey = buildReturnKey(searchParams);
  const lastHandledKey = useRef<string | null>(null);

  useEffect(() => {
    if (!returnKey) {
      lastHandledKey.current = null;
      return;
    }

    const workspaceId = readTikTokOAuthWorkspace() ?? defaultWorkspaceId;
    if (!workspaceId) return;
    if (lastHandledKey.current === returnKey) return;

    lastHandledKey.current = returnKey;

    void (async () => {
      try {
        await refreshTikTokConnectionAfterOAuthReturn(queryClient, workspaceId);
      } finally {
        clearTikTokOAuthFlow();
        const params = new URLSearchParams(searchParams?.toString() ?? "");
        params.delete("tiktok");
        params.delete("oauth");
        router.replace(toStaticExportHref(pathname, params.toString()));
      }
    })();
  }, [
    defaultWorkspaceId,
    pathname,
    queryClient,
    returnKey,
    router,
    searchParams,
  ]);

  return null;
}
